/* import __COLOCATED_TEMPLATE__ from './filter-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type OptimizeSuggestionsService from 'embercom/services/optimize-suggestions-service';
import type IntlService from 'ember-intl/services/intl';
import type { OptimizeSuggestionFilterState } from '../lib/optimize-suggestion-filter-helper';

interface Signature {
  Args: {
    onFilterChange: () => void;
    onClose: () => void;
  };
}

export default class FilterPanel extends Component<Signature> {
  @service declare optimizeSuggestionsService: OptimizeSuggestionsService;
  @service declare intl: IntlService;

  @tracked private localReasonTypes: string[];
  @tracked private localDateRange: string;
  @tracked private localTopics: string[];

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.localReasonTypes = this.initialReasonTypes;
    this.localDateRange = this.initialDateRange;
    this.localTopics = this.initialTopics;
  }

  get initialReasonTypes(): string[] {
    let types = this.optimizeSuggestionsService.filterState.suggestionType;
    return types.length === 0 ? [] : types;
  }

  get initialDateRange(): string {
    return this.optimizeSuggestionsService.filterState.date || 'all';
  }

  get initialTopics(): string[] {
    return this.optimizeSuggestionsService.filterState.topics || [];
  }

  @action
  handleReasonChange(value: string): void {
    if (value === 'all') {
      this.localReasonTypes = [];
    } else if (this.localReasonTypes.includes(value)) {
      this.localReasonTypes = this.localReasonTypes.filter((type) => type !== value);
    } else {
      this.localReasonTypes = [...this.localReasonTypes, value];
    }
  }

  @action
  handleDateRangeChange(value: string): void {
    this.localDateRange = value;
  }

  @action
  handleTopicChange(value: string): void {
    if (value === 'all') {
      this.localTopics = [];
    } else if (this.localTopics.includes(value)) {
      this.localTopics = this.localTopics.filter((topic) => topic !== value);
    } else {
      this.localTopics = [...this.localTopics, value];
    }
  }

  @action
  handleApplyFilters(closePanel: () => void): void {
    this.optimizeSuggestionsService.setFilterState(this.currentFilterState);
    this.args.onFilterChange();
    closePanel();
  }

  get currentFilterState(): OptimizeSuggestionFilterState {
    let suggestionType: ('content-gap' | 'data-gap' | 'action-gap')[] = this.localReasonTypes as
      | ('content-gap' | 'data-gap' | 'action-gap')[]
      | [];

    let date: string | undefined = this.localDateRange === 'all' ? undefined : this.localDateRange;

    let topics: string[] = this.localTopics.length === 0 ? [] : this.localTopics;

    return {
      suggestionType,
      date,
      topics,
    };
  }

  get predictedSuggestionsCount(): number {
    return this.optimizeSuggestionsService.predictedSuggestionsCount(this.currentFilterState);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::FilterPanel': typeof FilterPanel;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/filter-panel': typeof FilterPanel;
  }
}
